import React, { useState } from "react"
import NCTable from "../../shared/table/NCTable"
import NCText from "../../shared/NCText"
import { Grid, Box, IconButton, useTheme, useMediaQuery } from "@material-ui/core"
import { useGetDrivers, useGetDriversFile } from "../../services/queries"
import { downloadFileFromResponse, formatDate } from "../../libs/utils"
import {  SEARCH_CONFIG, TABLE_CONFIG } from "../../libs/config"
import NCInput from "../../shared/NCInput"
import NCButton from "../../shared/NCButton"
import ExportMenu from "../../components/ExportMenu"
import {
  AddIcon,
  SearchIcon,
  DetailIcon,
  ReviveIcon,
  DeleteIcon
} from "../../libs/icons"
import NCSvgIcon from "../../shared/NCSvgIcon"
import { useNavigate } from "react-router-dom"
import NCLoader from "../../shared/NCLoader"
import NCModal from "../../shared/NCModal"
import { toast } from "react-toastify"
import { useQueryClient } from "react-query"
import { useDeleteDriver, useActivateDriver } from "../../services/mutations"
import useDebounce from "../../libs/hooks/Debounce"
import NotificationAlert from "../../components/notification/NotificationAlert"
import getQueryParams from "../../libs/hooks/useQuery"
import { useTranslation } from "react-i18next"

const DriverList = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState("")
  const [pageSize, setPageSize] = useState(TABLE_CONFIG.PAGE_SIZE)
  const [ordering, setOrdering] = useState("-created")
  const [statusFilter, setStatusFilter] = useState([])
  const [downloadType, setDownloadType] = useState()
  const [showDeleteModel, setShowDeleteModel] = useState(false)
  const [selectedDriverId, setSelectedDriverId] = useState()
  let queryParams = getQueryParams()
  const nav = useNavigate()
  const debouncedSearchTerm = useDebounce(query, SEARCH_CONFIG.TIME_IN_MILLI)

  const deleteDriverApi = useDeleteDriver()
  const activateDriverApi = useActivateDriver()
  const queryClient = useQueryClient()

  const mobileTheme = useTheme()
  const isSmallScreen = useMediaQuery(mobileTheme.breakpoints.down("sm"))

  const { isLoading, data, refetch: refetchDriverList } = useGetDrivers(
    page,
    debouncedSearchTerm,
    pageSize,
    {
      ordering,
      status:
        statusFilter && statusFilter.length
          ? statusFilter[0].value.toString()
          : null,
      dispatcher: queryParams.get("dispatcher")
    }
  )
  const { refetch } = useGetDriversFile(
    downloadType,
    query,
    { ordering },
    {
      enabled: false
    }
  )

  const {
    next,
    previous,
    total_pages,
    results: driverData
  } = data?.data?.data || {}

  const onNext = () => {
    setPage(prev => (next ? prev + 1 : prev))
  }
  const onPrev = () => {
    setPage(prev => (previous ? prev - 1 : prev))
  }
  const deleteDriver = driverId => {
    deleteDriverApi.mutate(driverId, {
      onSuccess: () => {
        toast.success(t("drivers.deleted"))
        queryClient.invalidateQueries(["getDriverList"])
        setShowDeleteModel(false)
        refetchDriverList()
      }
    })
  }
  const handleDeleteClick = cell => {
    if (cell.status === "O") {
      //reactivate
      activateDriverApi.mutate(cell.id, {
        onSuccess: () => {
          toast.success(t("drivers.activate"))
          queryClient.refetchQueries(["getDriverList"])
          setShowDeleteModel(false)
          refetchDriverList()
        }
      })
    } else {
      setSelectedDriverId(cell.id)
      setShowDeleteModel(true)
    }
  }
  const getActionColumn = cell => {
    return (
      <div className="action-icons">
        <Box display={"flex"} alignItems={"center"}>
          <IconButton onClick={() => {
            handleDeleteClick(cell) 
          }}>
            {cell.status === "O" ? <ReviveIcon /> : <DeleteIcon />}
          </IconButton>
          <IconButton onClick={() => nav(`/drivers/${cell.id}`)}>
            <DetailIcon />
          </IconButton>
        </Box>
      </div>
    )
  }

  const getDetailsByClientName = cell => {
    //   console.log(cell)
    return (
      <div onClick={() => nav(`/drivers/${cell.id}`)}>
        <NCText className={"clickable-text"}>{cell?.name}</NCText>
      </div>
    )
  }

  const columns = [
    {
      Header: t("list.driver_id"),
      id: "id",
      accessor: cell => cell.id,
      Cell: ({ value }) => (
        <NCText
          className={"clickable-text"}
          onClick={() => nav(`/drivers/${value}`)}
        >
          {value}
        </NCText>
      ),
      disableSortBy: false
    },
    {
      Header: t("list.driver_name"),
      id: "name",
      accessor: cell => getDetailsByClientName(cell)
    },
    {
      Header: t("list.driver_type"),
      id: "type",
      disableSortBy: true,
      accessor: cell => cell.driver_type
    },
    {
      Header: t("list.status"),
      id: "driver_profile_status",
      disableSortBy: true,
      accessor: cell => cell.driver_profile_status,
      Cell: ({ value }) => (
        <NCText>{(value == 1 || value == 2) ? "Pending" : value == 3 ? "Approved" : "Denied"}</NCText>
      ),
    },
    {
      Header: t("list.email"),
      accessor: "email"
    },
    {
      Header: t("list.phone_number"),
      accessor: "phone"
    },
    {
      Header:t("list.date_created"),
      id: "created",
      accessor: cell => cell.created,
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: "",
      id: "action",
      accessor: cell => getActionColumn(cell),
      disableSortBy: true
    }
  ]
  const downloadFile = async type => {
    await setDownloadType(type)
    const data = await refetch()
    const contentDispositionHeaders =
      data?.data?.headers["content-disposition"].split("=")
    downloadFileFromResponse(
      data?.data?.data,
      contentDispositionHeaders[contentDispositionHeaders.length - 1]
        .replaceAll(/[/"]/g, "")
        .trim()
    )
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Grid container justifyContent="space-between" mb={20}>
            <Grid item xs={12} md={4}>
              {!isSmallScreen ? (
                <NCText variant={"h4"}>{t("drivers.heading")}</NCText>
              ) : (
                <Grid container justifyContent="space-between">
                  <Grid item style={{ paddingLeft: "0" }}>
                    <NCText variant={"h4"}>{t("drivers.heading")}</NCText>
                  </Grid>
                  <Grid item style={{ paddingLeft: "0" }}>
                    <NotificationAlert />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2} justifyContent={isSmallScreen ? "flex-start" : "flex-end"}>
                <Grid item>
                  <NCInput
                    value={query}
                    onChange={e => {
                      setPage(1)
                      setOrdering(null)
                      setQuery(e.target.value)
                    }}
                    placeholder={t("common.search_here")}
                    startIcon={SearchIcon}
                    className="rounded"
                  />
                </Grid>
                <Grid item>
                  <ExportMenu itemAction={item => downloadFile(item.key)} />
                </Grid>
                <Grid item style={{ paddingRight: "0px" }}>
                  <NCButton
                    variant="contained"
                    color="primary"
                    endIcon={<NCSvgIcon component={AddIcon} inheritViewBox />}
                    onClick={() => nav("/drivers/add-driver")}
                  >
                    {t("drivers.add_driver")}
                  </NCButton>
                </Grid>
                {!isSmallScreen && (
                  <Grid item style={{ paddingLeft: "0" }}>
                    <NotificationAlert />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <NCTable
          columns={columns}
          data={driverData || []}
          page={page}
          onNext={onNext}
          onPrev={onPrev}
          pageCount={total_pages}
          gotoPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          sorting={ordering}
          setSorting={setOrdering}
          filter={statusFilter}
          setFilter={setStatusFilter}
          isLoading={isLoading}
        />
        {isLoading && <NCLoader open={true} />}
      </Grid>
      <NCModal
        open={showDeleteModel}
        title={t("common.delete")}
        text={t("drivers.delete_confirm")}
        onClose={() => setShowDeleteModel(false)}
        submitButton={
          <NCButton
            variant="contained"
            color="secondary"
            disableElevation
            onClick={() => deleteDriver(selectedDriverId)}
            style={{ width: "auto" }}
          >
            {t("common.yes")}
          </NCButton>
        }
      />
    </Grid>
  )
}
export default DriverList
